import { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../services/firebase';
import { doc, onSnapshot, collection, query, where, getDocs, limit as firestoreLimit } from 'firebase/firestore';
import './RegressionDashboard.css';

/**
 * Identification Accuracy Dashboard
 * Triggers regression suite and displays historical performance trends.
 */
function RegressionDashboard() {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const [stats, setStats] = useState(null);
    const [progress, setProgress] = useState(null);
    const [failures, setFailures] = useState([]);
    const [showFailures, setShowFailures] = useState(false);

    const functions = getFunctions(undefined, 'europe-west1');
    const runRegression = httpsCallable(functions, 'runRegression', { timeout: 540000 }); // 9 minutes
    const fetchHistory = httpsCallable(functions, 'getRegressionHistory');

    useEffect(() => {
        loadHistory();
        loadFailures();

        const unsubProgress = onSnapshot(doc(db, 'config', 'regressionProgress'), (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                if (data.running) {
                    // Stalled detection (older than 15 minutes)
                    const lastUpdated = data.lastUpdated?.toDate ? data.lastUpdated.toDate() : (data.lastUpdated ? new Date(data.lastUpdated) : new Date());
                    const now = new Date();
                    const diffMinutes = (now - lastUpdated) / (1000 * 60);

                    if (diffMinutes > 15) {
                        setProgress({ ...data, stalled: true });
                        setRunning(false);
                    } else {
                        setProgress(data);
                        setRunning(true);
                    }
                } else {
                    setProgress(null);
                    setRunning(false);
                }
            }
        });

        return () => unsubProgress();
    }, []);

    const loadHistory = async () => {
        setLoading(true);
        try {
            const { data } = await fetchHistory({ limit: 3 });
            setHistory(data || []);
            if (data && data.length > 0) {
                setStats(data[0]); // Latest run
            }
        } catch (err) {
            console.error('Failed to load regression history:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadFailures = async () => {
        try {
            const q = query(
                collection(db, 'regression_samples'),
                where('status', '==', 'labeled'),
                firestoreLimit(50)
            );
            const snap = await getDocs(q);
            const allLabeled = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            // Filter failures (rank > 1 or rank == -1)
            const failed = allLabeled.filter(s => 
                s.lastEvaluation && (s.lastEvaluation.brandRank > 1 || s.lastEvaluation.brandRank === -1)
            ).sort((a, b) => (b.lastEvaluation?.runAt?.toMillis() || 0) - (a.lastEvaluation?.runAt?.toMillis() || 0));
            
            setFailures(failed);
        } catch (err) {
            console.error('Failed to load failures:', err);
        }
    };

    const handleRunTest = async () => {
        const description = prompt("Enter description for this test run (optional):", "Standard Audit");
        if (description === null) return;

        setRunning(true);
        setProgress({ processed: 0, total: stats?.samplesCount || 47, running: true });

        try {
            const sessionId = `session_${Date.now()}`;
            let offset = 0;
            let result;

            // Loop through chunks until done
            do {
                const response = await runRegression({ description, offset, sessionId });
                result = response.data;
                offset = result.nextOffset || offset;
            } while (result && !result.done);

            await Promise.all([loadHistory(), loadFailures()]);
            const pct = result?.metrics ? (result.metrics.precision1Brand * 100).toFixed(1) : '?';
            alert(`Regression Suite Complete!\nBrand Precision@1: ${pct}%`);
        } catch (err) {
            console.error('Test run failed:', err);
            alert("Accuracy test failed: " + err.message);
        } finally {
            setRunning(false);
            setProgress(null);
        }
    };

    const calculateDelta = (metricName) => {
        if (history.length < 2) return null;
        const current = history[0].metrics[metricName];
        const previous = history[1].metrics[metricName];
        const delta = ((current - previous) * 100).toFixed(1);
        return {
            value: (delta > 0 ? '+' : '') + delta + '%',
            class: delta > 0 ? 'delta-positive' : (delta < 0 ? 'delta-negative' : 'delta-neutral')
        };
    };

    if (loading && history.length === 0) {
        return <div className="loading-overlay">Loading Accuracy Metrics...</div>;
    }

    const latest = stats || (history.length > 0 ? history[0] : null);

    return (
        <div className="regression-dashboard">
            <div className="regression-header">
                <h2>Identification Accuracy</h2>
                <button 
                    className="run-test-btn" 
                    onClick={handleRunTest} 
                    disabled={running}
                >
                    {running ? (
                        <>
                            <span className="loading-spinner small" />
                            {progress ? `Running (${progress.processed}/${progress.total})` : 'Running Suite...'}
                        </>
                    ) : (
                        <>🧪 Run Regression Suite</>
                    )}
                </button>
            </div>

            {progress && (
                <div className={`progress-container ${progress.stalled ? 'stalled' : ''}`}>
                    <div className="progress-text">
                        <span>
                            {progress.stalled ? (
                                <strong>⚠️ Stalled at: </strong>
                            ) : (
                                <strong>Testing: </strong>
                            )}
                            {progress.currentSample || '...'}
                        </span>
                        <span>{Math.round((progress.processed / (progress.total || 1)) * 100)}%</span>
                    </div>
                    <div className="reg-progress-bg">
                        <div 
                            className={`reg-progress-fill ${progress.stalled ? 'stalled' : ''}`}
                            style={{ width: `${(progress.processed / (progress.total || 1)) * 100}%` }} 
                        />
                    </div>
                    {progress.stalled && (
                        <div className="stalled-warning">
                            The previous test run appears to have stalled. You can safely click "Run Regression Suite" above to start a new one.
                        </div>
                    )}
                </div>
            )}

            {latest ? (
                <div className="metrics-summary-grid">
                    <div className="metric-box">
                        <span className="metric-label">Brand Precision @1</span>
                        <div className="metric-value-row">
                            <span className="metric-value">{(latest.metrics.precision1Brand * 100).toFixed(0)}%</span>
                            {calculateDelta('precision1Brand') && (
                                <span className={`metric-delta ${calculateDelta('precision1Brand').class}`}>
                                    {calculateDelta('precision1Brand').value}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="metric-box">
                        <span className="metric-label">Brand Precision @5</span>
                        <div className="metric-value-row">
                            <span className="metric-value">{(latest.metrics.precision5Brand * 100).toFixed(0)}%</span>
                            {calculateDelta('precision5Brand') && (
                                <span className={`metric-delta ${calculateDelta('precision5Brand').class}`}>
                                    {calculateDelta('precision5Brand').value}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="metric-box">
                        <span className="metric-label">Miss Rate</span>
                        <div className="metric-value-row">
                            <span className="metric-value" style={{ color: latest.metrics.missRate > 0.3 ? '#f44336' : 'inherit' }}>
                                {(latest.metrics.missRate * 100).toFixed(0)}%
                            </span>
                             {calculateDelta('missRate') && (
                                <span className={`metric-delta ${calculateDelta('missRate').class === 'delta-positive' ? 'delta-negative' : (calculateDelta('missRate').class === 'delta-negative' ? 'delta-positive' : 'delta-neutral')}`}>
                                    {calculateDelta('missRate').value}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="health-item" style={{ justifyContent: 'center', padding: '40px' }}>
                    No test data available. Run the regression suite to establish a baseline.
                </div>
            )}

            <div className="regression-history">
                <div style={{ padding: '15px', fontWeight: 'bold', borderBottom: '1px solid #eee' }}>Run History</div>
                <table>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Description</th>
                            <th>Samples</th>
                            <th>Pr@1 (Brand)</th>
                            <th>Pr@5 (Brand)</th>
                            <th>Miss %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {history.map((run) => {
                            let dateStr = "N/A";
                            try {
                                const ts = run.timestamp;
                                if (ts) {
                                    let d;
                                    if (typeof ts === 'string') {
                                        d = new Date(ts);
                                    } else if (ts._seconds) {
                                        d = new Date(ts._seconds * 1000);
                                    } else if (ts.seconds) {
                                        d = new Date(ts.seconds * 1000);
                                    } else {
                                        d = new Date(ts);
                                    }
                                    
                                    if (d instanceof Date && !isNaN(d.getTime())) {
                                        dateStr = d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                    }
                                }
                            } catch (e) {
                                console.warn("Date parse error", e);
                            }

                            return (
                                <tr key={run.id}>
                                    <td>{dateStr}</td>
                                    <td>
                                        {run.metadata.description}
                                        {run.metadata.isBaseline && <span className="status-badge status-baseline" style={{ marginLeft: '8px' }}>Baseline</span>}
                                    </td>
                                    <td>{run.samplesCount}</td>
                                    <td>{(run.metrics.precision1Brand * 100).toFixed(1)}%</td>
                                    <td>{(run.metrics.precision5Brand * 100).toFixed(1)}%</td>
                                    <td>{(run.metrics.missRate * 100).toFixed(1)}%</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {failures.length > 0 && (
                <div className="failures-section">
                    <div className="failures-header" onClick={() => setShowFailures(!showFailures)}>
                        <span>⚠️ Recent Failures & Edge Cases ({failures.length})</span>
                        <span>{showFailures ? '▲' : '▼'}</span>
                    </div>
                    {showFailures && (
                        <table className="failures-table">
                            <thead>
                                <tr>
                                    <th>Sample</th>
                                    <th>Expected</th>
                                    <th>Top Pick</th>
                                    <th>Rank</th>
                                </tr>
                            </thead>
                            <tbody>
                                {failures.map(fail => (
                                    <tr key={fail.id}>
                                        <td>
                                            <img src={fail.uploadedImageUrl} alt="" className="fail-thumb" />
                                        </td>
                                        <td>{fail.label?.correctManufacturer}</td>
                                        <td>{fail.lastEvaluation?.pipelinePicked || 'None'}</td>
                                        <td>
                                            <span className={`fail-rank ${fail.lastEvaluation?.brandRank === -1 ? 'rank-miss' : 'rank-low'}`}>
                                                {fail.lastEvaluation?.brandRank === -1 ? 'Miss' : `Rank ${fail.lastEvaluation?.brandRank}`}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
}

export default RegressionDashboard;
